import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './select';
import { useTenantSettings } from '../../hooks/useTenantSettings';

/**
 * TimeField — inline HH / MM (+ AM/PM) dropdown time picker.
 *
 * Respects the tenant's Display Preference (Settings › Rules): when time_format
 * is "12h" it shows 1-12 hours plus an AM/PM selector; otherwise 00-23.
 * The VALUE is always emitted/consumed as a 24-hour "HH:mm" string so the data
 * model is unchanged regardless of the display preference.
 *
 * @param {string}   value      - "HH:mm" (24-hour) or empty string
 * @param {function} onChange   - (value: "HH:mm") => void  ("" when cleared)
 * @param {boolean}  allowNone  - show a "-" option to clear the field
 * @param {string}   testId     - data-testid prefix
 */
const MINUTES = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

export function TimeField({ value, onChange, allowNone = false, testId = 'time-field' }) {
  const { time_format } = useTenantSettings();
  const is12h = time_format === '12h';

  const has = !!value && /^\d{1,2}:\d{2}/.test(value);
  const h24 = has ? parseInt(value.split(':')[0], 10) : null;
  const min = has ? value.split(':')[1] : null;

  const period = h24 == null ? 'AM' : (h24 >= 12 ? 'PM' : 'AM');
  const dispHour = h24 == null ? null : (is12h ? ((h24 % 12) || 12) : h24);
  const dispHourStr = dispHour == null ? '' : dispHour.toString().padStart(2, '0');

  const buildValue = (dHour, dMin, per) => {
    let hh;
    if (is12h) {
      let n = parseInt(dHour, 10) % 12;
      if (per === 'PM') n += 12;
      hh = n.toString().padStart(2, '0');
    } else {
      hh = parseInt(dHour, 10).toString().padStart(2, '0');
    }
    return `${hh}:${dMin || '00'}`;
  };

  const onHour = (hr) => {
    if (hr === 'none') { onChange(''); return; }
    onChange(buildValue(hr, has ? min : '00', period));
  };
  const onMinute = (m) => {
    if (m === 'none') { onChange(''); return; }
    onChange(buildValue(dispHourStr || '07', m, period));
  };
  const onPeriod = (p) => {
    onChange(buildValue(dispHourStr || '07', has ? min : '00', p));
  };

  const hourOptions = is12h
    ? Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'))
    : Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));

  return (
    <div className="flex items-center gap-1" data-testid={testId}>
      <Select value={has ? dispHourStr : 'none'} onValueChange={onHour}>
        <SelectTrigger className="w-20 font-mono" data-testid={`${testId}-hour`}>
          <SelectValue placeholder="HH" />
        </SelectTrigger>
        <SelectContent>
          {allowNone && <SelectItem value="none">-</SelectItem>}
          {hourOptions.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
        </SelectContent>
      </Select>
      <span className="text-muted-foreground font-mono">:</span>
      <Select value={has ? min : 'none'} onValueChange={onMinute}>
        <SelectTrigger className="w-20 font-mono" data-testid={`${testId}-minute`}>
          <SelectValue placeholder="MM" />
        </SelectTrigger>
        <SelectContent>
          {allowNone && <SelectItem value="none">-</SelectItem>}
          {MINUTES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
        </SelectContent>
      </Select>
      {is12h && (
        <Select value={period} onValueChange={onPeriod}>
          <SelectTrigger className="w-[4.5rem] font-mono" data-testid={`${testId}-period`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="AM">AM</SelectItem>
            <SelectItem value="PM">PM</SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

export default TimeField;
